import { jsxs, jsx } from 'react/jsx-runtime';
import { client } from 'braintree-web';
import classNames from 'classnames';
import { forwardRef, useRef, useState, useCallback, useEffect, useImperativeHandle } from 'react';
import { LockMid } from '@zooplus/icons';
import { Alert, Checkbox } from '@zooplus/ui-components';
import CardIcon from '../CardIcon/CardIcon.js';
import CVVIcon from '../CVVIcon/CVVIcon.js';
import FormControl from '../FormControl/FormControl.js';
import FormControlWrapper from '../FormControlWrapper/FormControlWrapper.js';
import useIsMobile from '../../hooks/useIsMobile.js';
import { useTranslation } from '../../hooks/useTranslations.js';
import { useConfig } from '../../providers/ConfigProvider.js';
import { useErrorContext } from '../../providers/ErrorContextProvider.js';
import { sanitizeCardType, sanitizeCardTypes } from '../../services/braintreeService.js';
import { braintreeTracking } from '../../services/diiService.js';
import { captureException } from '../../services/sentryService.js';
import { useBraintreeHostedFields, braintreeTokenizeCard, getBraintreeStatusCallbacks } from '../../utils/braintreeUtils.js';
import styles from './BraintreeCreditCardForm.module.scss.js';

const DISPLAYABLE_CARD_TYPES = [
    'visa',
    'mastercard',
    'diners-club',
    'american-express',
    'maestro',
];
const BraintreeCreditCardForm = forwardRef(({ token, containerType, error, labelsInline, allowVaulting, onFatalError }, forwardedRef) => {
    const braintreeInstance = useRef();
    const hostedFieldsInstance = useRef();
    const { t } = useTranslation();
    const { setError } = useErrorContext();
    const { config: { env }, } = useConfig();
    const isLocalEnv = env === 'local';
    const isMobile = useIsMobile();
    const [storeInVault, setStoreInVault] = useState(false);
    const [supportedCardTypes, setSupportedCardTypes] = useState([]);
    const [hostedFieldsCards, setHostedFieldsCards] = useState([]);
    const [intialized, setInitialized] = useState(false);
    const [fieldsValidity, setFieldsValidity] = useState({});
    const [fieldsEmpty, setFieldsEmpty] = useState({});
    const makeHostedFields = useBraintreeHostedFields(braintreeInstance);
    const commonCards = hostedFieldsCards.filter((element) => supportedCardTypes.includes(sanitizeCardType(element.type)));
    const selectedCardType = commonCards.length == 1 ? sanitizeCardType(commonCards[0].type) : null;
    const updateValidity = (newFlagByFieldName) => {
        setFieldsValidity((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
    };
    const updateEmpty = (newFlagByFieldName) => {
        setFieldsEmpty((oldFlagByFieldName) => ({ ...oldFlagByFieldName, ...newFlagByFieldName }));
    };
    const { isInvalid, isInvalidEmpty, isCardTypeActive } = getBraintreeStatusCallbacks(intialized, fieldsValidity, hostedFieldsCards, supportedCardTypes, fieldsEmpty);
    // Since braintree validity does not take supported card types into account
    // (a card can be of an ussuported type but considered valid anyway)
    // We need a manual validation of card type
    const isCurrentCardNumberSupported = useCallback(() => {
        return (!intialized ||
            !!hostedFieldsCards.find((card) => supportedCardTypes.includes(sanitizeCardType(card.type))));
    }, [intialized, hostedFieldsCards, supportedCardTypes]);
    const registerHostedFieldsCallbacks = useCallback(() => {
        hostedFieldsInstance.current?.on('validityChange', (event) => {
            if (isLocalEnv)
                console.log('Card validity changed :', event.fields);
            updateValidity({ [event.emittedBy]: event.fields[event.emittedBy]?.isValid });
        });
        hostedFieldsInstance.current?.on('empty', (event) => {
            if (isLocalEnv)
                console.log('Card field empty changed :', event.fields);
            updateEmpty({ [event.emittedBy]: event.fields[event.emittedBy]?.isEmpty });
        });
        hostedFieldsInstance.current?.on('notEmpty', (event) => {
            if (isLocalEnv)
                console.log('Card field empty changed :', event.fields);
            updateEmpty({ [event.emittedBy]: event.fields[event.emittedBy]?.isEmpty });
        });
        hostedFieldsInstance.current?.on('cardTypeChange', (event) => {
            if (isLocalEnv)
                console.log('Card type changed :', event.cards);
            setHostedFieldsCards(event.cards);
        });
    }, []);
    const handleFatalError = useCallback((error) => {
        onFatalError?.(error);
        setError({
            message: t('checkout.payment.creditcard.error.method.unavailable'),
            retryable: false,
        });
    }, [onFatalError, t, setError]);
    const createHostedFields = useCallback(async () => {
        if (isLocalEnv)
            console.log('Creating new hosted fields instance...');
        try {
            hostedFieldsInstance.current = await makeHostedFields();
            if (isLocalEnv)
                console.log('Braintree hosted fields instance created.');
            setHostedFieldsCards(hostedFieldsInstance.current.getState().cards);
            const types = await hostedFieldsInstance.current.getSupportedCardTypes();
            if (isLocalEnv)
                console.log('Supported card nice types', types);
            const cardTypes = sanitizeCardTypes(types);
            if (isLocalEnv)
                console.log('Supported card types', cardTypes);
            setSupportedCardTypes(cardTypes);
            registerHostedFieldsCallbacks();
            setInitialized(true);
        }
        catch (error) {
            switch (error.code) {
                case 'HOSTED_FIELDS_FIELD_DUPLICATE_IFRAME':
                    braintreeTracking('BTformKO', error.message);
                    break;
                default:
                    captureException({ error, message: 'Braintree hosted fields cannot be created' });
                    handleFatalError(error);
                    break;
            }
        }
    }, [t, registerHostedFieldsCallbacks, handleFatalError]);
    useEffect(() => {
        (async () => {
            try {
                if (isLocalEnv)
                    console.log('Creating new braintree instance...');
                braintreeInstance.current = await client.create({ authorization: token });
            }
            catch (error) {
                // error.code = 'INSTANTIATION_OPTION_REQUIRED' when token is not set
                // error.code = 'CLIENT_AUTHORIZATION_INVALID' when token is not valid
                handleFatalError(error);
                captureException({ error, message: 'Cannot create Braintree client' });
                throw error;
            }
            createHostedFields();
        })();
    }, [createHostedFields, handleFatalError, token]);
    const tokenizeCard = useCallback(braintreeTokenizeCard({
        setError,
        hostedFieldsInstance,
        isCurrentCardNumberSupported,
        updateValidity,
        updateEmpty,
        isLocalEnv,
        storeInVault,
        containerType,
    }), [isCurrentCardNumberSupported, setError, storeInVault, containerType]);
    useEffect(() => {
        if (Object.values(fieldsValidity).every((v) => v)) {
            setError(undefined);
        }
    }, [fieldsValidity, setError]);
    // For calling from within Ract application
    useImperativeHandle(forwardedRef, () => ({
        tokenizeCard,
    }));
    const handleStoreInVaultChange = (event) => {
        setStoreInVault(event.target.checked);
    };
    const isAmex = hostedFieldsCards.length === 1 && isCardTypeActive('american-express');
    const isSupported = isCurrentCardNumberSupported();
    return (jsxs("div", { "data-zta": "braintree-cc-form-container", children: [error && (jsx(Alert, { className: styles.errorMessage, variant: "warning", children: error })), jsx("div", { className: styles.activeCards, children: selectedCardType
                    ? isMobile && (jsx(CardIcon, { type: selectedCardType, className: styles.selectedCard }, selectedCardType))
                    : DISPLAYABLE_CARD_TYPES.filter((type, supported) => supportedCardTypes.includes(type)).map((type) => {
                        return (jsx(CardIcon, { type: type, className: classNames(isCardTypeActive(type) && styles.activeCard) }, type));
                    }) }), jsxs("div", { "data-zta": "braintree-cc-form", children: [jsx(FormControlWrapper, { label: t('checkout.payment.creditcard.card_number'), labelFor: "cc-number", labelInline: labelsInline, infoMessage: t('checkout.payment.secure'), infoMessageIcon: jsx(LockMid, {}), errorMessage: isInvalidEmpty('number')
                            ? t('checkout.form.required.error')
                            : !isSupported
                                ? t('checkout.payment.creditcard.type_not_supported')
                                : isInvalid('number') &&
                                    t('checkout.payment.creditcard.error.form.cardnumber.invalid'), errorZta: "errorInputCreditCardNumber", children: jsx(FormControl, { innerClassname: styles.noPadding, id: "cc-number", appendixItems: selectedCardType &&
                                !isMobile && (jsx(CardIcon, { type: selectedCardType, className: styles.selectedCard }, selectedCardType)), hasError: !isSupported || isInvalid('number') || isInvalidEmpty('number'), "data-zta": "inputCreditCardNumber" }) }), jsx(FormControlWrapper, { label: t('checkout.payment.creditcard.holder_name'), labelFor: "cardholder-name", labelInline: labelsInline, errorMessage: isInvalidEmpty('cardholderName') && t('checkout.form.required.error'), errorZta: "errorInputCardName", children: jsx(FormControl, { innerClassname: styles.noPadding, hasError: isInvalid('cardholderName') || isInvalidEmpty('cardholderName'), id: "cardholder-name", "data-zta": "inputCardName" }) }), jsx(FormControlWrapper, { label: t('checkout.payment.creditcard.expiry_date'), labelFor: "expiration-month", labelInline: labelsInline, errorMessage: (isInvalidEmpty('expirationMonth') || isInvalidEmpty('expirationYear')) &&
                            t('checkout.form.required.error'), errorZta: "errorInputCardDate", children: jsxs("div", { className: styles.expirationDateContainer, children: [jsx(FormControl, { select: true, className: styles.expirationDateMonth, hasError: isInvalid('expirationMonth') || isInvalidEmpty('expirationMonth'), id: "expiration-month", "data-zta": "inputCardMonth" }), jsx(FormControl, { select: true, className: styles.expirationDateYear, hasError: isInvalid('expirationYear') || isInvalidEmpty('expirationYear'), id: "expiration-year", "data-zta": "inputCardYear" })] }) }), jsx(FormControlWrapper, { label: isAmex
                            ? t('checkout.payment.creditcard.cid.generic_code')
                            : t('checkout.payment.creditcard.cvc.generic_code'), labelFor: "cc-cvv", labelInline: labelsInline, infoMessage: isAmex
                            ? t('checkout.payment.creditcard.cid.warning')
                            : t('checkout.payment.creditcard.cvc.warning'), errorMessage: isInvalid('cvv')
                            ? t('checkout.form.cvc.min.length.error', { '0': isAmex ? 4 : 3 })
                            : isInvalidEmpty('cvv') && t('checkout.form.required.error'), errorZta: "errorInputCardCode", children: jsx(FormControl, { className: styles.cvv, innerClassname: styles.noPadding, id: "cc-cvv", appendixItems: jsx(CVVIcon, { isAmex: isAmex }), hasError: isInvalid('cvv') || isInvalidEmpty('cvv'), "data-zta": "inputCardCode" }) }), allowVaulting && (jsx(FormControlWrapper, { labelInline: labelsInline, children: jsx(Checkbox, { className: styles.vaultCheck, id: "vault-check", checked: storeInVault, onChange: handleStoreInVaultChange, "data-zta": "storeCreditCard", children: t('checkout.payment.save.creditcard') }) }))] })] }));
});

export { BraintreeCreditCardForm as default };
//# sourceMappingURL=BraintreeCreditCardForm.js.map
